import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../Homepage/Navbar';
import Footer from '../Homepage/Footer';
import { Base_URL, imageBaseURL } from '../../actions/API_endpoint';

const GalleryDetail = () => {
    const { id } = useParams();
    const [gallery, setGallery] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lightboxIndex, setLightboxIndex] = useState(null);

    useEffect(() => {
        const fetchGallery = async () => {
            try {
                const response = await axios.get(`${Base_URL}/api/galleries/${id}`);
                if (response.data.success) {
                    setGallery(response.data.data);
                }
            } catch (error) {
                console.error("Error fetching gallery:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchGallery();
    }, [id]);

    const openLightbox = (index) => {
        setLightboxIndex(index);
        document.body.style.overflow = 'hidden'; // Prevent scrolling when modal is open
    };

    const closeLightbox = () => {
        setLightboxIndex(null);
        document.body.style.overflow = 'unset';
    };

    const nextImage = useCallback((e) => {
        e?.stopPropagation();
        if (gallery && gallery.images) {
            setLightboxIndex((prev) => (prev + 1) % gallery.images.length);
        }
    }, [gallery]);

    const prevImage = useCallback((e) => {
        e?.stopPropagation();
        if (gallery && gallery.images) {
            setLightboxIndex((prev) => (prev - 1 + gallery.images.length) % gallery.images.length);
        }
    }, [gallery]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (lightboxIndex === null) return;
            if (e.key === 'Escape') closeLightbox();
            if (e.key === 'ArrowRight') nextImage();
            if (e.key === 'ArrowLeft') prevImage();
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [lightboxIndex, nextImage, prevImage]);

    if (loading) {
        return (
            <div className="min-h-screen flex flex-col bg-dark-secondary">
                <Navbar />
                <main className="flex-grow flex items-center justify-center">
                    <div className="text-white text-xl">Loading...</div>
                </main>
                <Footer />
            </div>
        );
    }

    if (!gallery) {
        return (
            <div className="min-h-screen flex flex-col bg-dark-secondary">
                <Navbar />
                <main className="flex-grow flex items-center justify-center">
                    <div className="text-white text-xl">Gallery not found.</div>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col bg-dark-secondary">
            <Navbar />
            <main className="flex-grow pt-32 pb-20">
                <div className="container mx-auto px-4">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10">
                        <div>
                            <Link to="/gallery" className="text-accent-primary hover:text-accent-hover mb-2 inline-block font-semibold">
                                &larr; Back to Galleries
                            </Link>
                            <h1 className="text-3xl md:text-5xl font-extrabold text-white">
                                {gallery.name}
                            </h1>
                            {gallery.description && (
                                <p className="text-gray-400 mt-2 max-w-2xl">
                                    {gallery.description}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        {gallery.images && gallery.images.length > 0 ? (
                            gallery.images.map((img, index) => (
                                <div
                                    key={img.id}
                                    onClick={() => openLightbox(index)}
                                    className="relative group rounded-xl overflow-hidden shadow-md hover:shadow-xl border border-white/5 bg-dark-tertiary cursor-pointer h-64 transition-all duration-300 hover:-translate-y-1"
                                >
                                    <img
                                        src={`${imageBaseURL}${img.image_path}`}
                                        alt={img.caption || gallery.name}
                                        className="w-full h-full object-cover transform transition-transform duration-700 group-hover:scale-110"
                                    />
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                                        <i className="fas fa-expand text-white text-3xl drop-shadow-lg transform scale-50 group-hover:scale-100 transition-transform duration-300"></i>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="text-gray-400 col-span-full text-center py-10">No images in this gallery.</p>
                        )}
                    </div>
                </div>
            </main>

            <Footer />

            {/* Lightbox Modal - Rendered via Portal */}
            {lightboxIndex !== null && gallery.images && createPortal(
                <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black w-screen h-screen">

                    {/* Close Button */}
                    <button
                        onClick={closeLightbox}
                        className="absolute top-5 right-5 z-[100000] bg-white hover:bg-gray-200 text-black transition-all rounded-full p-2 shadow-2xl flex items-center justify-center border-4 border-red-500 cursor-pointer"
                        style={{ width: '60px', height: '60px' }}
                        title="Close (Esc)"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" className="w-8 h-8">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    {/* Left Arrow - Only show if more than 1 image */}
                    {gallery.images.length > 1 && (
                        <button
                            onClick={prevImage}
                            className="fixed left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors bg-white/10 hover:bg-white/20 rounded-full p-4 cursor-pointer backdrop-blur-sm border border-white/20 hover:scale-110 active:scale-95 duration-200"
                            style={{ zIndex: 100002 }}
                            title="Previous (Left Arrow)"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" className="w-10 h-10 drop-shadow-md">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                            </svg>
                        </button>
                    )}

                    {/* Right Arrow - Only show if more than 1 image */}
                    {gallery.images.length > 1 && (
                        <button
                            onClick={nextImage}
                            className="fixed right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors bg-white/10 hover:bg-white/20 rounded-full p-4 cursor-pointer backdrop-blur-sm border border-white/20 hover:scale-110 active:scale-95 duration-200"
                            style={{ zIndex: 100002 }}
                            title="Next (Click to Advance)"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" className="w-10 h-10 drop-shadow-md">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                            </svg>
                        </button>
                    )}

                    {/* Image Container */}
                    <div className="relative max-w-[95vw] max-h-[95vh] w-full flex items-center justify-center p-2 z-[99999]" onClick={(e) => e.stopPropagation()}>
                        <img
                            src={`${imageBaseURL}${gallery.images[lightboxIndex].image_path}`}
                            alt={gallery.images[lightboxIndex].caption || "Gallery Image"}
                            className="max-h-[90vh] max-w-full object-contain rounded shadow-2xl select-none cursor-pointer"
                            onClick={nextImage}
                        />
                        {/* Caption */}
                        {gallery.images[lightboxIndex].caption && (
                            <div className="absolute bottom-4 left-0 right-0 text-center text-white text-xl bg-black/60 py-2 rounded pointer-events-none">
                                {gallery.images[lightboxIndex].caption}
                            </div>
                        )}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default GalleryDetail;
